import { useState } from 'react';
import { createPortal } from 'react-dom';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

// Letters whose spoken name starts with a vowel sound, so acronym brands get
// the right article: "an MXR" (em-ex-ar), "an EHX", but "a JHS", "a TC".
const VOWEL_SOUND_LETTERS = 'AEFHILMNORSX';

// Vowel-initial words that are actually spoken with a leading consonant sound,
// so they take "a" rather than "an" — "a Universal Audio".
const CONSONANT_SOUND_VOWEL_START = /^(uni|use|eu|one|ubi|uti)/i;

const articleFor = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return 'a';

  const firstWord = trimmed.split(/\s+/)[0];
  if (/^[A-Z0-9-]{2,}$/.test(firstWord)) {
    return VOWEL_SOUND_LETTERS.includes(firstWord[0].toUpperCase()) ? 'an' : 'a';
  }

  if (CONSONANT_SOUND_VOWEL_START.test(trimmed)) return 'a';
  return /^[aeiou]/i.test(trimmed) ? 'an' : 'a';
};

const pedalName = (item) => `${item.pedalMake} ${item.pedalModel}`.trim();

/**
 * Offers to clear wants that a just-completed trade or purchase satisfied.
 *
 * Fires immediately after feedback is submitted, driven by the `wantListPrompt`
 * array that submitFeedback returns. Nothing is persisted — if the user closes
 * the app instead of answering, the prompt is simply gone and they can still
 * clean up their Wantlist by hand.
 *
 * Only the responder in a multi-pedal trade can ever see more than one row.
 */
const WantListCleanupModal = ({ items = [], tradeType = 'trade', onClose }) => {
  const [selected, setSelected] = useState(() => new Set(items.map((i) => i.wantId)));
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  if (!items.length) return null;

  const isMulti = items.length > 1;
  const verb = tradeType === 'purchase' ? 'purchased' : 'traded for';
  const first = items[0];

  const toggle = (wantId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(wantId)) next.delete(wantId);
      else next.add(wantId);
      return next;
    });
  };

  const handleRemove = async () => {
    if (!selected.size || removing) return;
    setRemoving(true);
    setError('');

    // Deleting the want is enough — cleanupMatchesOnWantDelete tears down the
    // associated matches and wantsMyPedals rows server-side.
    const results = await Promise.allSettled(
      [...selected].map((wantId) => deleteDoc(doc(db, 'wantList', wantId)))
    );

    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      console.error('Failed to remove Wantlist entries:', failed.map((f) => f.reason));
      setRemoving(false);
      setError(
        failed.length === results.length
          ? "We couldn't update your Wantlist. You can remove it from the Wantlist page."
          : "Some entries couldn't be removed. Check your Wantlist page."
      );
      return;
    }

    setRemoving(false);
    onClose();
  };

  const handleOverlayClick = () => {
    if (!removing) onClose();
  };

  // Portalled to <body> on purpose. Both mount points sit inside the progress
  // timeline's `.tp-tl-panel`, which is `position: relative; z-index: 1` — a
  // stacking context. Rendered in place, the overlay's z-index resolves inside
  // that context and the bottom tab bar (z-index 1002) paints over the modal.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Update your Wantlist"
      className="wlc-overlay"
      onClick={handleOverlayClick}
    >
      <style>{`
        .wlc-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0, 0, 0, 0.52);
          z-index: 2100;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          font-family: Nunito, sans-serif;
          animation: wlc-overlay-in 0.25s ease forwards;
        }
        @keyframes wlc-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes wlc-sheet-in {
          from { opacity: 0; transform: translateY(36px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        .wlc-sheet {
          background: #fff;
          border-radius: 25px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.18);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: wlc-sheet-in 0.35s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
        }
        .wlc-header {
          border-bottom: 1px solid #e5e7eb;
          padding: 22px 24px 18px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
        }
        .wlc-title {
          margin: 0;
          font-size: 1.18rem;
          font-weight: 800;
          color: #1f2937;
          line-height: 1.4;
        }
        .wlc-close {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 22px;
          line-height: 1;
          color: #9CA3AF;
          padding: 2px 4px;
          flex-shrink: 0;
        }
        .wlc-body {
          padding: 18px 24px 4px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .wlc-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: #fff;
          text-align: left;
        }
        .wlc-row-selectable {
          cursor: pointer;
          width: 100%;
          font-family: Nunito, sans-serif;
        }
        .wlc-row-selectable.wlc-on {
          border-color: #C89F3C;
          background: #FDFAF2;
        }
        /* Circle + white tick, matching the brand checkmark used across the app
           and the selection affordance in OfferPage's bulk-send modal. Filled
           with this modal's accent when selected. */
        .wlc-check {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 2px solid #d1d5db;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
        }
        .wlc-check.wlc-on {
          background: #C89F3C;
          border-color: #C89F3C;
        }
        .wlc-thumb {
          width: 46px;
          height: 46px;
          border-radius: 10px;
          object-fit: cover;
          background: #F3F4F6;
          flex-shrink: 0;
        }
        .wlc-thumb-empty {
          width: 46px;
          height: 46px;
          border-radius: 10px;
          background: #F3F4F6;
          flex-shrink: 0;
        }
        .wlc-pedal {
          font-size: 0.98rem;
          font-weight: 700;
          color: #374151;
          min-width: 0;
          overflow-wrap: anywhere;
        }
        .wlc-error {
          margin: 10px 24px 0;
          color: #dc2626;
          font-size: 0.85rem;
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 8px 12px;
        }
        .wlc-cta-wrap {
          padding: 18px 24px 24px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .wlc-primary {
          display: block;
          width: 100%;
          padding: 14px;
          border-radius: 9999px;
          border: none;
          background-color: #C89F3C;
          color: #ffffff;
          font-family: Nunito, sans-serif;
          font-size: 1.05rem;
          font-weight: 800;
          cursor: pointer;
          letter-spacing: 0.01em;
        }
        .wlc-primary:disabled {
          background-color: #f3f4f6;
          color: #9ca3af;
          cursor: not-allowed;
        }
        .wlc-dismiss {
          background: none;
          border: none;
          cursor: pointer;
          font-family: Nunito, sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          color: #9CA3AF;
          text-align: center;
          padding: 4px 0;
        }
        @media (max-width: 768px) {
          .wlc-sheet { border-radius: 18px; max-width: 100%; }
          .wlc-header { padding: 16px 18px 14px; }
          .wlc-title { font-size: 1.05rem; }
          .wlc-body { padding: 14px 18px 4px; }
          .wlc-error { margin: 10px 18px 0; }
          .wlc-cta-wrap { padding: 14px 18px 20px; }
        }
      `}</style>

      <div className="wlc-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="wlc-header">
          <h2 className="wlc-title">
            {isMulti
              ? `Now that you've ${verb} these, do you want to remove them from your Wantlist?`
              : `Now that you've ${verb} ${articleFor(pedalName(first))} ${pedalName(first)}, do you want to remove it from your Wantlist?`}
          </h2>
          <button type="button" className="wlc-close" onClick={onClose} aria-label="Close" disabled={removing}>
            ×
          </button>
        </div>

        <div className="wlc-body">
          {items.map((item) => {
            const name = pedalName(item);
            const thumb = item.imageUrl
              ? <img className="wlc-thumb" src={item.imageUrl} alt="" />
              : <div className="wlc-thumb-empty" />;

            if (!isMulti) {
              return (
                <div key={item.wantId} className="wlc-row">
                  {thumb}
                  <span className="wlc-pedal">{name}</span>
                </div>
              );
            }

            const on = selected.has(item.wantId);
            return (
              <button
                key={item.wantId}
                type="button"
                className={`wlc-row wlc-row-selectable${on ? ' wlc-on' : ''}`}
                onClick={() => toggle(item.wantId)}
                aria-pressed={on}
                disabled={removing}
              >
                <span className={`wlc-check${on ? ' wlc-on' : ''}`}>
                  {on && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {thumb}
                <span className="wlc-pedal">{name}</span>
              </button>
            );
          })}
        </div>

        {error && <div className="wlc-error">{error}</div>}

        <div className="wlc-cta-wrap">
          <button
            type="button"
            className="wlc-primary"
            onClick={handleRemove}
            disabled={removing || selected.size === 0}
          >
            {removing
              ? 'Removing...'
              : isMulti
                ? `Remove ${selected.size} from Wantlist`
                : 'Remove from Wantlist'}
          </button>
          <button type="button" className="wlc-dismiss" onClick={onClose} disabled={removing}>
            {isMulti ? 'Keep them' : 'Keep it'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WantListCleanupModal;
